import { Avatar, Box, Chip, ColorPaletteProp, Stack, Typography } from "@mui/joy";
import SocialIcon, { SocialIconType } from "./SocialIcon";
import { AgentObj } from "@/types/interfaces/agents";
import {
  AssuredWorkloadOutlined,
  CreditCardOutlined,
  DriveFileRenameOutlineOutlined,
  EventOutlined,
  LocalPhoneOutlined,
  LockOutlined,
  LockPersonOutlined,
  MailOutlineOutlined,
  PlaceOutlined,
  TimelineOutlined,
} from "@mui/icons-material";
import LinkButton from "@/components/LinkButton/LinkButton";
import { agentBillingUrl } from "@/pages/admin/billing/utils";
import { ReactNode } from "react";
import CopyButton from "./CopyButton";

type IntroPanelProps = {
  agent: Omit<AgentObj, "_id"> | null;
};

const STATUS_COLORS: Record<string, ColorPaletteProp> = {
  ACTIVE: "success",
  NEW: "primary",
  LEAVING: "warning",
  RESIGNED: "danger",
};

const getRoleLabel = (agent: IntroPanelProps["agent"]) => {
  if (agent?.role === "STAFF") return "Support Office Staff";
  if (agent?.role === "AGENT") return agent.agentType === "PA" ? "Primary Agent" : "Associate Agent";
  return null;
};

const toTitleCase = (value: string) => value.charAt(0) + value.slice(1).toLowerCase();

function MetaItem({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.75}
      sx={{ color: "text.secondary", "--Icon-color": "currentColor", "--Icon-fontSize": "18px" }}
    >
      {icon}
      <Typography level="body-sm" textColor="text.secondary" component="div">
        {children}
      </Typography>
    </Stack>
  );
}

function IntroPanel({ agent }: IntroPanelProps) {
  const emailParam = encodeURIComponent(agent?.ttandEmail || "");
  const fullName = [agent?.firstname, agent?.lastname].filter(Boolean).join(" ") || "-";
  const initials = [agent?.firstname?.[0], agent?.lastname?.[0]].filter(Boolean).join("").toUpperCase();
  const roleLabel = getRoleLabel(agent);
  const location = [agent?.city, agent?.province].filter(Boolean).join(", ");

  const socialLinks: { type: SocialIconType; link?: string }[] = [
    { type: "website", link: agent?.websiteURL },
    { type: "facebook", link: agent?.facebookUrl },
    { type: "twitter", link: agent?.twitterUrl },
    { type: "youtube", link: agent?.youtubeUrl },
    { type: "instagram", link: agent?.instagramUrl },
    { type: "linkedin", link: agent?.linkedinUrl },
    { type: "pinterest", link: agent?.pinterestUrl },
    { type: "tiktok", link: agent?.tiktokUrl },
  ];
  const availableSocialLinks = socialLinks.filter((social) => !!social.link?.trim());

  const secondaryActions = [
    { label: "Changelogs", to: `/admin/agents/changelog?email=${emailParam}`, icon: <TimelineOutlined /> },
    {
      label: "Financials",
      to: `/admin/agents/finance-dashboard?email=${emailParam}`,
      icon: <AssuredWorkloadOutlined />,
    },
    { label: "Supplier Signs", to: `/admin/agents/supplier-signs?email=${emailParam}`, icon: <LockPersonOutlined /> },
    // Invoices, balance and recurring charges; that page links on to the billing settings in Edit Profile
    { label: "Billing", to: agentBillingUrl(agent?.ttandEmail || ""), icon: <CreditCardOutlined /> },
  ];

  return (
    <Box
      sx={{
        background: agent?.secretAgent
          ? "var(--ttand-admin-palette-gradient-danger)"
          : "var(--ttand-admin-palette-gradient-active)",
        px: { xs: 2, md: 4 },
        pt: 3.5,
        pb: 3,
      }}
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", lg: "center" }}
        gap={3}
      >
        <Stack direction="row" alignItems="center" gap={2.5} sx={{ minWidth: 0 }}>
          <Avatar
            src={agent?.profilePicture || undefined}
            alt={fullName}
            sx={{
              width: 104,
              height: 104,
              fontSize: "2rem",
              border: "4px solid",
              borderColor: "common.white",
              boxShadow: "md",
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>

          <Stack gap={1} sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography level="h2" sx={{ fontSize: { xs: "xl2", md: "xl3" } }}>
                {agent?.title ? `${agent.title}. ` : ""}
                {fullName}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
              {roleLabel && (
                <Chip size="sm" variant="solid" color="neutral">
                  {roleLabel}
                </Chip>
              )}
              {agent?.status && (
                <Chip size="sm" variant="solid" color={STATUS_COLORS[agent.status] || "neutral"}>
                  {toTitleCase(agent.status)}
                </Chip>
              )}
              {agent?.secretAgent && (
                <Chip size="sm" variant="solid" color="danger" startDecorator={<LockOutlined />}>
                  Private Agent
                </Chip>
              )}
              {agent?.isLuxeAgent && (
                <Chip size="sm" variant="soft" color="warning">
                  LUXE
                </Chip>
              )}
            </Stack>

            <Stack direction="row" alignItems="center" columnGap={2.5} rowGap={0.5} flexWrap="wrap">
              {agent?.ttandEmail && (
                <MetaItem icon={<MailOutlineOutlined />}>
                  <Stack direction="row" alignItems="center" gap={0.25}>
                    {agent.ttandEmail}
                    <CopyButton value={agent.ttandEmail} label="Copy email" />
                  </Stack>
                </MetaItem>
              )}
              {agent?.ttandExtension && <MetaItem icon={<LocalPhoneOutlined />}>Ext. {agent.ttandExtension}</MetaItem>}
              {location && <MetaItem icon={<PlaceOutlined />}>{location}</MetaItem>}
              {agent?.originalJoinDate && <MetaItem icon={<EventOutlined />}>Joined {agent.originalJoinDate}</MetaItem>}
            </Stack>

            {availableSocialLinks.length > 0 && (
              <Stack direction="row" gap={0.75}>
                {availableSocialLinks.map((social) => (
                  <SocialIcon key={social.type} type={social.type} link={social.link || ""} />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>

        <Stack gap={1.25} alignItems={{ xs: "flex-start", lg: "flex-end" }} sx={{ flexShrink: 0 }}>
          <LinkButton
            to={`/admin/agents/edit?email=${emailParam}`}
            startDecorator={<DriveFileRenameOutlineOutlined />}
            sx={{ boxShadow: "sm" }}
          >
            Edit Profile
          </LinkButton>
          <Stack direction="row" gap={1} flexWrap="wrap" justifyContent={{ lg: "flex-end" }}>
            {secondaryActions.map((action) => (
              <LinkButton
                key={action.label}
                to={action.to}
                size="sm"
                variant="outlined"
                color="neutral"
                startDecorator={action.icon}
                sx={{
                  backgroundColor: "background.surface",
                  "&:hover": { backgroundColor: "background.level1" },
                }}
              >
                {action.label}
              </LinkButton>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}

export default IntroPanel;
